import React, { ReactElement, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Typography from "../../../../../components/typography";
import { Link } from "react-router-dom";
import { Colors } from "../../../../../utils/colors";

import TrackListItem from "../track-list-item";
import api from "services/axiosInstance";
import { GetActiveArtist } from "services/user/getUser";
import { ReleaseContainerWithUrls, SongWithUrls } from "types/response-type";

import DiscographyEmptyState from "./discography-empty-state";
import Toast from "components/toast";
import Spinner from "components/spinner";
import { IUserArtist } from "types/user";

const OverviewDiscography = () => {
  const [songs, setSongs] = useState<
    (SongWithUrls | ReleaseContainerWithUrls)[]
  >([]);

  const [loading, setLoading] = useState(false);
  const artist = GetActiveArtist();

  const GetDiscography = async () => {
    if (!artist) {
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.get(`/songs/artist/${artist?.id}/discography`);

      if (!data.data) {
        return;
      }

      const { singles, albums, eps } = data.data;
      const singleData: SongWithUrls[] = singles;
      const albumData: ReleaseContainerWithUrls[] = albums;
      const epData: ReleaseContainerWithUrls[] = eps;
      const discography = [...singleData, ...albumData, ...epData];

      setSongs(discography);
    } catch (error) {
      Toast({ error });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    GetDiscography();
  }, []);

  const AddDisabled = useMemo(() => {
    if (!artist) {
      return true;
    }
    return false;
  }, [artist]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }
  return (
    <Container>
      <Header>
        <Typography weight="medium" size="2xl">
          Discography
        </Typography>
        {songs.length > 0 ? (
          <TextLink to={"/overview/discography"}>
            <Typography color={Colors.yellow400}>See all</Typography>
          </TextLink>
        ) : null}
      </Header>
      {songs.length > 0 ? (
        <TableContainer>
          <thead>
            <tr>
              <th scope="col">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "9.4rem"
                  }}
                >
                  <Typography size="s" weight="semibold" mr="0.4">
                    Title
                  </Typography>
                  <Arrow>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="6"
                      height="7"
                      viewBox="0 0 6 7"
                      fill="none"
                    >
                      <path
                        d="M4.75 4.25L3 2.75L1.25 4.25"
                        stroke="#909090"
                        stroke-width="0.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="6"
                      height="7"
                      viewBox="0 0 6 7"
                      fill="none"
                    >
                      <path
                        d="M4.75 2.75L3 4.25L1.25 2.75"
                        stroke="#909090"
                        stroke-width="0.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </Arrow>
                </div>
              </th>
              <th scope="col">
                <Typography size="s" weight="medium" mr="0.4">
                  Type
                </Typography>
              </th>
              <th scope="col">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Typography size="s" weight="semibold" mr="0.4">
                    No of Tracks
                  </Typography>
                  <Arrow>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="6"
                      height="7"
                      viewBox="0 0 6 7"
                      fill="none"
                    >
                      <path
                        d="M4.75 4.25L3 2.75L1.25 4.25"
                        stroke="#909090"
                        stroke-width="0.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="6"
                      height="7"
                      viewBox="0 0 6 7"
                      fill="none"
                    >
                      <path
                        d="M4.75 2.75L3 4.25L1.25 2.75"
                        stroke="#909090"
                        stroke-width="0.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </Arrow>
                </div>
              </th>
              <th scope="col">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Typography size="s" weight="semibold" mr="0.4">
                    Release Date
                  </Typography>
                  <Arrow>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="6"
                      height="7"
                      viewBox="0 0 6 7"
                      fill="none"
                    >
                      <path
                        d="M4.75 4.25L3 2.75L1.25 4.25"
                        stroke="#909090"
                        stroke-width="0.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="6"
                      height="7"
                      viewBox="0 0 6 7"
                      fill="none"
                    >
                      <path
                        d="M4.75 2.75L3 4.25L1.25 2.75"
                        stroke="#909090"
                        stroke-width="0.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </Arrow>
                </div>
              </th>
              <th scope="col">
                <Typography size="s" weight="medium" mr="0.4">
                  Status
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            {songs?.map((song, i) => {
              if (i > 4) {
                return null;
              }
              return <TrackListItem data={song} key={i} />;
            })}
          </tbody>
        </TableContainer>
      ) : (
        <DiscographyEmptyState AddDisabled={AddDisabled} />
      )}
    </Container>
  );
};

export default OverviewDiscography;

const Container = styled.div`
  margin-bottom: 3.2rem;
  position: relative;
  @media only screen and (max-width: 769px) {
    overflow-x: scroll;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 30rem;
`;

const Header = styled.div`
  display: flex;
  margin-bottom: 1.4rem;
  align-items: end;
  justify-content: space-between;
`;
const TextLink = styled(Link)`
  all: unset;
  cursor: pointer;
`;

const TableContainer = styled.table`
  margin-bottom: 6.4rem;
  width: 100%;
  color: white;
  border-collapse: collapse;
  @media only screen and (max-width: 769px) {
    width: 100rem;
    overflow: scroll;
  }

  tr {
    background-color: transparent;
    position: relative;
    &:nth-child(odd) {
      background-color: ${Colors.grey700};
    }
  }
  th {
    text-align: left;
    color: ${Colors.grey500};
    padding: 1.65rem 0;
    background-color: ${Colors.grey900};
    cursor: auto;
  }

  td {
    color: #e4e4e4;
    padding: 1.2rem 0;
  }
`;

const Arrow = styled.div`
  display: flex;
  flex-direction: column;
`;
